import { useEffect, useState } from 'react';
import { isAbort } from '../../api/http';
import { toApiError } from '../../api/errors';
import type { CollectionDef } from './collections';
import { countTicketsUsing, type VocabRow } from './settings-api';

/**
 * 🔴 THE CONSEQUENCE, ON THE ROW, BEFORE THE CLICK.
 *
 * "47 tickets" next to Deactivate is the difference between a decision and a surprise. The
 * alternative — click, get a 409, read "still in use" — explains the consequence AFTERWARDS, by which
 * point the person has already decided and is now arguing with the machine.
 *
 * ⭐⭐ AND A COUNT WE DO NOT HAVE IS PRINTED AS "unknown", NEVER AS 0. `GET /tickets` filters by
 * `status`, `type` and `priority` and by nothing else — there is no impact, lane or resolution
 * filter. A zero for those would read as "nothing uses this, safe to retire", which is the exact
 * wrong conclusion to hand somebody one click before a consequential action, and it would be wrong
 * silently.
 */
export type Usage =
  /** A real number from the API's own `total`. */
  | { kind: 'count'; count: number; noun: string }
  /** No endpoint can answer this. `why` is printed, so the gap is legible rather than mysterious. */
  | { kind: 'unavailable'; why: string }
  | { kind: 'loading' }
  /** We asked and the request failed — distinct from "nothing can answer it". */
  | { kind: 'failed'; why: string };

export function usageLabel(usage: Usage): string {
  switch (usage.kind) {
    case 'count':
      return `${usage.count} ${usage.count === 1 ? usage.noun.replace(/e?s$/, '') : usage.noun}`;
    case 'loading':
      return 'counting…';
    case 'unavailable':
      return 'usage unknown';
    case 'failed':
      return 'usage unknown';
  }
}

/**
 * Lanes are counted from the STATUSES, not from tickets.
 *
 * ⭐ Not a workaround for the missing filter — it is the truer number. Retiring a lane does not
 * orphan tickets, it orphans the statuses that name it, and that is what the board groups by. A
 * ticket count would answer a question nobody asked about lanes.
 */
export function laneUsage(statuses: readonly VocabRow[], laneCode: string): Usage {
  const count = statuses.filter((status) => status.lane === laneCode).length;
  return { kind: 'count', count, noun: 'statuses' };
}

const UNAVAILABLE_REASON: Record<string, string> = {
  impacts: 'GET /tickets has no `impact` filter, so nothing can count these without inventing a number.',
  resolutions:
    'GET /tickets has no `resolution` filter, so nothing can count these without inventing a number.',
  modules:
    'GET /tickets has no `module` filter — `ticket_module` is a side table with no count endpoint.',
};

export function unavailableUsage(collection: string): Usage {
  return {
    kind: 'unavailable',
    why:
      UNAVAILABLE_REASON[collection] ??
      'The API offers no filter that could count usage for this collection.',
  };
}

/**
 * Usage counts for one collection, one request per row.
 *
 * ⚠️ THAT IS 26 REQUESTS FOR THE STATUSES TAB, and it is a deliberate trade. Settings is a
 * low-frequency admin screen, `limit=1` makes each response tiny, and the alternative is either a
 * count endpoint that does not exist or a number that is not real. ⛔ It is not fetched for the
 * collections that cannot be counted, so nothing is spent finding out what we already know.
 *
 * ⭐ Counts are over ALL PROJECTS — no `projectId` is sent. A status retired globally is used by
 * whatever uses it anywhere, and scoping the count to the current project would understate the
 * consequence of a global action. The panel says so on screen.
 */
export function useUsageCounts(def: CollectionDef, rows: readonly VocabRow[]) {
  const [usage, setUsage] = useState<Map<string, Usage>>(new Map());
  const codesKey = rows.map((row) => row.code).join(',');
  const filter = def.usageFilter;

  /**
   * ⭐⭐ THE DEPENDENCY IS THE CODES, NOT THE ARRAY. `useResource` hands out a new array on every
   * refetch and the parent re-renders on every keystroke in the add form; keying this effect on the
   * array identity would re-issue 26 requests each time, and — because the effect calls setState —
   * each answer would trigger the next round. That is not a slow screen, it is a request loop.
   */
  useEffect(() => {
    const codes = codesKey ? codesKey.split(',') : [];
    if (!filter || codes.length === 0) {
      // ⛔ Guarded, not unconditional: an unconditional `setUsage(new Map())` here is a fresh object
      //    every run, which re-renders, which runs the effect again.
      setUsage((previous) => (previous.size === 0 ? previous : new Map()));
      return;
    }

    const controller = new AbortController();
    setUsage(new Map(codes.map((code) => [code, { kind: 'loading' } as Usage])));

    let cancelled = false;
    void Promise.all(
      codes.map(async (code) => {
        try {
          const count = await countTicketsUsing(filter, code, controller.signal);
          return [code, { kind: 'count', count, noun: 'tickets' } as Usage] as const;
        } catch (err) {
          if (isAbort(err)) return [code, { kind: 'loading' } as Usage] as const;
          const apiError = toApiError(err, '/tickets');
          // ⛔ NOT zero. "We could not count" and "there are none" must not look the same on the row
          //    whose whole job is to show the consequence of retiring this value.
          return [code, { kind: 'failed', why: apiError.message } as Usage] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setUsage(new Map(entries));
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [filter, codesKey]);

  return usage;
}
